<template>
  <div class="modal-bg" @click.stop>
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>
          <i class="uil-check-circle"></i>
          Eğitimi Tamamla
        </h3>
        <button class="modal-close-btn" @click="closeModal">
          <i class="uil-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="contents-section">
          <h5>İçerikler</h5>
          <p class="text-muted mb-3">Tamamlanacak içerikleri seçin:</p>
          <div class="contents-list">
            <template v-for="(item, index) in contents">
              <!-- Program Item -->
              <div
                v-if="item.type === 'program'"
                :key="'program-' + item.id"
                class="program-group"
              >
                <!-- Program Header -->
                <div
                  class="program-header"
                  :class="{ 'completed': isProgramCompleted(item) }"
                  @click="!isProgramCompleted(item) && toggleProgram(item)"
                  :style="isProgramCompleted(item) ? 'cursor:not-allowed;' : 'cursor:pointer;'"
                >
                  <div class="content-checkbox" @click.stop>
                    <input
                      type="checkbox"
                      :id="'program-' + item.id"
                      :checked="isProgramFullySelected(item)"
                      :indeterminate.prop="isProgramPartiallySelected(item) && !isProgramFullySelected(item)"
                      @change="toggleProgram(item)"
                      :disabled="isProgramCompleted(item)"
                    >
                    <label :for="'program-' + item.id"></label>
                  </div>
                  <div class="program-icon">
                    <i class="uil-layer-group"></i>
                  </div>
                  <div class="program-image">
                    <img :src="item.cover_photo_link" :alt="item.name">
                  </div>
                  <div class="program-info">
                    <h6>{{ item.name }}</h6>
                    <div class="program-stats">
                      <span class="stat-item">
                        <i class="uil-check-circle"></i>
                        {{ getProgramCompletedCount(item) }}/{{ getProgramTotalCount(item) }} Tamamlandı
                      </span>
                      <span class="stat-item stat-selected">
                        <i class="uil-check"></i>
                        {{ getProgramSelectedCount(item) }}/{{ getProgramTotalCount(item) }} Seçili
                      </span>
                    </div>
                  </div>
                  <div class="program-toggle">
                    <i class="uil-angle-down"></i>
                  </div>
                </div>

                <!-- Program Sections -->
                <div class="program-sections">
                  <div
                    v-for="(section, sectionIndex) in item.sections"
                    :key="'section-' + section.id"
                    class="section-group"
                  >
                    <!-- Section Header -->
                    <div class="section-header">
                      <div class="section-checkbox" @click.stop>
                        <input
                          type="checkbox"
                          :id="'section-' + section.id"
                          :checked="isSectionFullySelected(item.id, section)"
                          :indeterminate.prop="isSectionPartiallySelected(item.id, section) && !isSectionFullySelected(item.id, section)"
                          @change="toggleSection(item.id, section)"
                          :disabled="isSectionCompleted(section)"
                        >
                        <label :for="'section-' + section.id"></label>
                      </div>
                      <div class="section-icon">
                        <i class="uil-folder"></i>
                      </div>
                      <div class="section-info">
                        <h6>{{ section.name }}</h6>
                        <span class="section-count">{{ section.contents.length }} İçerik</span>
                      </div>
                    </div>

                    <!-- Section Contents -->
                    <div class="section-contents">
                      <div
                        v-for="(content, contentIndex) in section.contents"
                        :key="'content-' + content.id"
                        class="content-item child-content"
                        :class="{
                          'selected': selectedContents.includes(content.id),
                          'completed': content.is_completed === true
                        }"
                        @click="!content.is_completed && toggleContent(content.id)"
                        style="cursor:pointer;"
                      >
                        <div class="content-checkbox" @click.stop>
                          <input
                            type="checkbox"
                            :id="'content-' + content.id"
                            :checked="selectedContents.includes(content.id)"
                            @change="toggleContent(content.id)"
                            :disabled="content.is_completed === true"
                          >
                          <label :for="'content-' + content.id"></label>
                        </div>
                        <div class="content-order">{{ contentIndex + 1 }}</div>
                        <div class="content-image">
                          <img :src="content.cover_photo_link" :alt="content.title">
                        </div>
                        <div class="content-info">
                          <h6>{{ content.title }}</h6>
                          <p>{{ content.description }}</p>
                          <div class="content-meta">
                            <span class="duration">
                              <i class="uil-clock"></i>
                              {{ content.duration }} dk
                            </span>
                            <span class="points">
                              <i class="uil-coins"></i>
                              {{ content.point }} puan
                            </span>
                            <span v-if="content.is_completed === true" class="completed-label">
                              <i class="uil-check"></i> Tamamlandı
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Standalone Content Item -->
              <div
                v-else
                :key="'content-' + item.id"
                class="content-item"
                :class="{
                  'selected': selectedContents.includes(item.id),
                  'completed': item.is_completed === true
                }"
                @click="!item.is_completed && toggleContent(item.id)"
                style="cursor:pointer;"
              >
                <div class="content-checkbox" @click.stop>
                  <input
                    type="checkbox"
                    :id="'content-' + item.id"
                    :checked="selectedContents.includes(item.id)"
                    @change="toggleContent(item.id)"
                    :disabled="item.is_completed === true"
                  >
                  <label :for="'content-' + item.id"></label>
                </div>
                <div class="content-order">{{ index + 1 }}</div>
                <div class="content-image">
                  <img :src="item.cover_photo_link" :alt="item.title">
                </div>
                <div class="content-info">
                  <h6>{{ item.title }}</h6>
                  <p>{{ item.description }}</p>
                  <div class="content-meta">
                    <span class="duration">
                      <i class="uil-clock"></i>
                      {{ item.duration }} dk
                    </span>
                    <span class="points">
                      <i class="uil-coins"></i>
                      {{ item.point }} puan
                    </span>
                    <span v-if="item.is_completed === true" class="completed-label">
                      <i class="uil-check"></i> Tamamlandı
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">
          İptal
        </button>
        <button 
          class="btn btn-primary" 
          @click="completeAssignment"
          :disabled="selectedContents.length === 0 || loading"
        >
          <i v-if="loading" class="uil-spinner"></i>
          {{ loading ? 'Tamamlanıyor...' : 'Tamamla' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CompleteAssignmentModal',
  props: {
    contents: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selectedContents: [],
      loading: false
    };
  },
  methods: {
    getProgramCompletedCount(program) {
      if (!program.sections || !Array.isArray(program.sections)) return 0;
      let count = 0;
      program.sections.forEach(section => {
        if (section.contents && Array.isArray(section.contents)) {
          count += section.contents.filter(c => c.is_completed === true).length;
        }
      });
      return count;
    },
    getProgramSelectedCount(program) {
      if (!program.sections || !Array.isArray(program.sections)) return 0;
      let count = 0;
      program.sections.forEach(section => {
        if (section.contents && Array.isArray(section.contents)) {
          count += section.contents.filter(c => this.selectedContents.includes(c.id)).length;
        }
      });
      return count;
    },
    getProgramTotalCount(program) {
      if (!program.sections || !Array.isArray(program.sections)) return 0;
      let count = 0;
      program.sections.forEach(section => {
        if (section.contents && Array.isArray(section.contents)) {
          count += section.contents.length;
        }
      });
      return count;
    },
    toggleSection(programId, section) {
      if (this.isSectionCompleted(section)) return;

      const sectionContentIds = section.contents
        .filter(c => c.is_completed !== true)
        .map(c => c.id);

      const allSelected = sectionContentIds.every(id =>
        this.selectedContents.includes(id)
      );

      if (allSelected) {
        // Tümü seçiliyse, tümünü kaldır
        sectionContentIds.forEach(id => {
          const index = this.selectedContents.indexOf(id);
          if (index > -1) {
            this.selectedContents.splice(index, 1);
          }
        });
      } else {
        // Değilse, tümünü seç
        sectionContentIds.forEach(id => {
          if (!this.selectedContents.includes(id)) {
            this.selectedContents.push(id);
          }
        });
      }
    },
    isSectionCompleted(section) {
      if (!section.contents || section.contents.length === 0) return false;
      return section.contents.every(c => c.is_completed === true);
    },
    isSectionFullySelected(programId, section) {
      const sectionContentIds = section.contents
        .filter(c => c.is_completed !== true)
        .map(c => c.id);

      if (sectionContentIds.length === 0) return false;

      return sectionContentIds.every(id =>
        this.selectedContents.includes(id)
      );
    },
    isSectionPartiallySelected(programId, section) {
      const sectionContentIds = section.contents
        .filter(c => c.is_completed !== true)
        .map(c => c.id);

      if (sectionContentIds.length === 0) return false;

      const selectedCount = sectionContentIds.filter(id =>
        this.selectedContents.includes(id)
      ).length;

      return selectedCount > 0 && selectedCount < sectionContentIds.length;
    },
    toggleContent(contentId) {
      const index = this.selectedContents.indexOf(contentId);
      if (index > -1) {
        this.selectedContents.splice(index, 1);
      } else {
        this.selectedContents.push(contentId);
      }
    },
    toggleProgram(program) {
      // Program tamamlanmışsa çık
      if (this.isProgramCompleted(program)) return;

      // Tüm section'lardaki content'leri topla
      let allContentIds = [];
      if (program.sections && Array.isArray(program.sections)) {
        program.sections.forEach(section => {
          if (section.contents && Array.isArray(section.contents)) {
            const contentIds = section.contents
              .filter(c => c.is_completed !== true)
              .map(c => c.id);
            allContentIds = allContentIds.concat(contentIds);
          }
        });
      }

      const allSelected = allContentIds.every(id =>
        this.selectedContents.includes(id)
      );

      if (allSelected) {
        // Tümü seçiliyse, tümünü kaldır
        allContentIds.forEach(id => {
          const index = this.selectedContents.indexOf(id);
          if (index > -1) {
            this.selectedContents.splice(index, 1);
          }
        });
      } else {
        // Değilse, tümünü seç
        allContentIds.forEach(id => {
          if (!this.selectedContents.includes(id)) {
            this.selectedContents.push(id);
          }
        });
      }
    },
    isProgramCompleted(program) {
      // Program kendisi tamamlanmış mı?
      if (program.is_completed === true) return true;

      // Program içindeki tüm section'lar ve content'ler tamamlanmış mı?
      if (program.sections && Array.isArray(program.sections)) {
        for (const section of program.sections) {
          if (section.contents && Array.isArray(section.contents)) {
            if (!section.contents.every(c => c.is_completed === true)) {
              return false;
            }
          }
        }
        return true;
      }

      return false;
    },
    isProgramFullySelected(program) {
      if (!program.sections || !Array.isArray(program.sections)) return false;

      let allContentIds = [];
      program.sections.forEach(section => {
        if (section.contents && Array.isArray(section.contents)) {
          const contentIds = section.contents
            .filter(c => c.is_completed !== true)
            .map(c => c.id);
          allContentIds = allContentIds.concat(contentIds);
        }
      });

      if (allContentIds.length === 0) return false;

      return allContentIds.every(id =>
        this.selectedContents.includes(id)
      );
    },
    isProgramPartiallySelected(program) {
      if (!program.sections || !Array.isArray(program.sections)) return false;

      let allContentIds = [];
      program.sections.forEach(section => {
        if (section.contents && Array.isArray(section.contents)) {
          const contentIds = section.contents
            .filter(c => c.is_completed !== true)
            .map(c => c.id);
          allContentIds = allContentIds.concat(contentIds);
        }
      });

      if (allContentIds.length === 0) return false;

      const selectedCount = allContentIds.filter(id =>
        this.selectedContents.includes(id)
      ).length;

      return selectedCount > 0 && selectedCount < allContentIds.length;
    },
    completeAssignment() {
      if (this.selectedContents.length === 0) return;

      // Seçili içerikleri topla (hem program içindekiler hem standalone)
      const selectedContentsData = [];

      this.contents.forEach(item => {
        if (item.type === 'program' && item.sections) {
          // Program içindeki section'lardaki content'leri kontrol et
          item.sections.forEach(section => {
            if (section.contents && Array.isArray(section.contents)) {
              section.contents.forEach(content => {
                if (this.selectedContents.includes(content.id)) {
                  selectedContentsData.push(content);
                }
              });
            }
          });
        } else if (this.selectedContents.includes(item.id)) {
          // Standalone content
          selectedContentsData.push(item);
        }
      });

      this.$emit('contents-selected', selectedContentsData);
    },
    closeModal() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-bg {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-header h3 i {
  color: #28a745;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.contents-section h5 {
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

.contents-list {
  width: 100%;
  overflow-y: auto;
}

.content-item {
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: 1.5px solid #e0e6ed;
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s cubic-bezier(.4,2,.6,1);
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.content-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.content-item.selected {
  border-color: #007bff;
  background: #f8f9ff;
}

.content-item.completed {
  opacity: 0.6;
  pointer-events: none;
  background: #f3f6f9;
}

/* Program Group Styles */
.program-group {
  margin-bottom: 16px;
}

.program-header {
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: 2px solid #a0d8f1;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: #0c4a6e;
}

.program-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(14, 165, 233, 0.2);
}

.program-header.completed {
  opacity: 0.7;
  pointer-events: none;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #86efac;
  color: #065f46;
  cursor: not-allowed !important;
}

.program-header.completed .program-icon {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.program-header.completed .program-info h6,
.program-header.completed .program-count {
  color: #065f46;
}

.program-header.completed .program-toggle {
  background: rgba(16, 185, 129, 0.1);
}

.program-header.completed .program-toggle i {
  color: #059669;
}

.program-header.completed .program-image {
  border-color: rgba(16, 185, 129, 0.3);
}

.program-header .content-checkbox label {
  border-color: #7dd3fc;
  background: white;
}

.program-header .content-checkbox input[type="checkbox"]:checked + label,
.program-header .content-checkbox input[type="checkbox"]:indeterminate + label {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.program-header .content-checkbox input[type="checkbox"]:checked + label::after {
  color: white;
}

.program-header .content-checkbox input[type="checkbox"]:indeterminate + label::after {
  content: '−';
  color: white;
}

.program-icon {
  background: rgba(14, 165, 233, 0.15);
  color: #0284c7;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.program-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  border: 2px solid rgba(14, 165, 233, 0.2);
}

.program-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.program-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.program-info h6 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #0c4a6e;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.program-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

.stat-item i {
  font-size: 13px;
}

.stat-selected {
  color: #0284c7;
  background: rgba(2, 132, 199, 0.1);
}

.program-toggle {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(14, 165, 233, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.program-toggle i {
  font-size: 20px;
  color: #0284c7;
}

/* Program Sections */
.program-sections {
  padding-left: 16px;
  margin-top: 8px;
}

.section-group {
  margin-bottom: 12px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 8px;
}

.section-header:hover {
  background: #f1f5f9;
}

.section-checkbox {
  position: relative;
  flex-shrink: 0;
}

.section-checkbox input[type="checkbox"] {
  display: none;
}

.section-checkbox label {
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid #cbd5e1;
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
}

.section-checkbox input[type="checkbox"]:checked + label {
  background: #3b82f6;
  border-color: #3b82f6;
}

.section-checkbox input[type="checkbox"]:checked + label::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 11px;
  font-weight: bold;
}

.section-checkbox input[type="checkbox"]:indeterminate + label {
  background: #f59e0b;
  border-color: #f59e0b;
}

.section-checkbox input[type="checkbox"]:indeterminate + label::after {
  content: '−';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 11px;
  font-weight: bold;
}

.section-icon {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.section-info {
  flex: 1;
  min-width: 0;
}

.section-info h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-count {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.section-contents {
  padding-left: 24px;
  position: relative;
}

.section-contents::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 8px;
  width: 2px;
  background: linear-gradient(180deg, #e2e8f0 0%, transparent 100%);
}

.child-content {
  position: relative;
  margin-bottom: 8px;
}

.child-content::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 50%;
  width: 12px;
  height: 2px;
  background: #e2e8f0;
}

.content-checkbox {
  position: relative;
  flex-shrink: 0;
}

.content-checkbox input[type="checkbox"] {
  display: none;
}

.content-checkbox label {
  display: block;
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
}

.content-checkbox input[type="checkbox"]:checked + label {
  background: #007bff;
  border-color: #007bff;
}

.content-checkbox input[type="checkbox"]:checked + label::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.content-order {
  background: #007bff;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

.content-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
}

.content-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.content-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.content-info h6 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content-info p {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #6c757d;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content-meta {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #6c757d;
}

.content-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.content-meta i {
  font-size: 12px;
}

.completed-label {
  color: #28a745;
  font-size: 12px;
  margin-left: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: #fff;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn-primary {
  background: #007bff;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn i {
  font-size: 14px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.uil-spinner {
  animation: spin 1s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .contents-grid {
    grid-template-columns: 1fr;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 16px;
  }

  .program-header {
    padding: 12px;
  }

  .program-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .program-image {
    width: 50px;
    height: 50px;
  }

  .program-info h6 {
    font-size: 14px;
  }

  .program-stats {
    gap: 8px;
  }

  .stat-item {
    font-size: 11px;
    padding: 3px 6px;
  }

  .stat-item i {
    font-size: 12px;
  }

  .program-sections {
    padding-left: 12px;
  }

  .section-header {
    padding: 8px 10px;
    gap: 8px;
  }

  .section-icon {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .section-info h6 {
    font-size: 13px;
  }

  .section-count {
    font-size: 11px;
  }

  .section-contents {
    padding-left: 20px;
  }

  .section-contents::before {
    left: 10px;
  }

  .child-content::before {
    left: -16px;
    width: 10px;
  }

  .content-order {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .content-image {
    width: 50px;
    height: 50px;
  }
}
</style> 